import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { ArrowLeft, ShieldCheck, Zap, Trophy, Award, Calendar, MessageSquare, RefreshCw, CheckCircle2, XCircle, AlertCircle, Pencil, Check, X } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminLeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem('cq_admin_token')) { navigate('/admin/login'); return; }
    (async () => {
      try {
        const res = await api.get(`/admin/leads/${id}`);
        setLead(res.data);
      } catch (e) {
        if (e?.response?.status === 401) { localStorage.removeItem('cq_admin_token'); navigate('/admin/login'); }
        else toast.error('Could not load lead');
      } finally { setLoading(false); }
    })();
  }, [id, navigate]);

  const resendReport = async () => {
    setResending(true);
    try {
      await api.post(`/admin/leads/${id}/resend-report`);
      toast.success('Report queued for regeneration + WhatsApp resend');
      setTimeout(async () => {
        try {
          const res = await api.get(`/admin/leads/${id}`);
          setLead(res.data);
        } catch { /* noop */ }
      }, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Resend failed');
    } finally {
      setResending(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-ink">Loading…</div>;
  if (!lead) return <div className="min-h-screen flex items-center justify-center text-muted-ink">Not found</div>;

  const r = lead.result || {};
  const primary = r.top_zones?.[0];

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <nav className="sticky top-0 z-30 bg-white border-b border-[#E2E8F0]">
        <div className="max-w-5xl mx-auto px-5 py-3 flex items-center justify-between">
          <Link to="/admin" data-testid="lead-detail-back" className="flex items-center gap-2 text-sm font-bold text-muted-ink hover:text-ink">
            <ArrowLeft className="w-4 h-4" strokeWidth={3} /> Back to leads
          </Link>
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-[#0055FF]" strokeWidth={2.5} />
            <div className="font-display font-extrabold text-ink text-sm">Lead Detail</div>
          </div>
          <div className="w-12" />
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-5 py-8 space-y-6">
        <div className="card-soft p-7">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#0055FF]">Student</div>
          <h1 className="font-display font-black text-ink text-3xl mt-1 tracking-tight">{lead.student_name}</h1>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-5 text-sm">
            <Info label="Grade" value={`Grade ${lead.grade}`} />
            <Info label="City" value={lead.city} />
            <Info label="Parent" value={lead.parent_name} />
            <Info label="Parent WhatsApp" value={lead.parent_whatsapp} />
            <Info label="Parent Email" value={lead.parent_email} />
            <Info label="Clarity level" value={`${lead.clarity_level} / 4`} />
            <Info label="Self archetype" value={lead.self_archetype} />
            <Info label="Submitted" value={(lead.created_at || '').slice(0, 19).replace('T', ' ')} />
          </div>
        </div>

        <div className="card-soft p-7" style={{ background: primary ? `linear-gradient(135deg, ${primary.color}14 0%, #ffffff 60%)` : undefined }}>
          <div className="flex items-center gap-4">
            {r.avatar?.image ? (
              <img src={r.avatar.image} alt={r.avatar.name} className="w-20 h-20 rounded-2xl object-cover shadow-lg" />
            ) : (
              <div className="w-20 h-20 rounded-2xl bg-[#FFD600] text-[#0F172A] flex items-center justify-center"><Trophy strokeWidth={2.5} /></div>
            )}
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-ink">Career Avatar</div>
              <div className="font-display font-black text-ink text-2xl">{r.avatar?.name || '—'}</div>
            </div>
          </div>
          <p className="text-body mt-3">{r.avatar?.line}</p>

          <div className="grid sm:grid-cols-3 gap-3 mt-5">
            {(r.top_zones || []).map((z, i) => (
              <ZoneCareersCard key={z.id} zone={z} index={i} leadId={id} onUpdated={(nextCareers) => {
                setLead((prev) => {
                  const next = { ...prev };
                  const zones = [...((next.result || {}).top_zones || [])];
                  zones[i] = { ...zones[i], careers: nextCareers };
                  next.result = { ...(next.result || {}), top_zones: zones };
                  return next;
                });
              }} />
            ))}
          </div>

          <div className="grid sm:grid-cols-2 gap-5 mt-6">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-ink">Top Traits</div>
              <ul className="mt-2 space-y-1 text-sm">
                {(r.top_traits || []).map((t) => (<li key={t.name} className="font-display font-extrabold text-ink">· {t.name} <span className="text-muted-ink font-bold">({t.score})</span></li>))}
              </ul>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-ink">Future Skill Stack</div>
              <ul className="mt-2 space-y-1 text-sm">
                {(r.skill_stack || []).map((s) => (<li key={s} className="text-body">· {s}</li>))}
              </ul>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            {r.guidance_level && (
              <span className="chip" style={{ background: r.guidance_level === 'High' ? '#FF5A00' : r.guidance_level === 'Medium' ? '#FFD600' : r.guidance_level === 'Light' ? '#0055FF' : '#F1F5F9', color: r.guidance_level === 'Medium' ? '#0F172A' : r.guidance_level === 'Low' ? '#64748B' : '#fff', borderColor: 'transparent' }}>
                Guidance: {r.guidance_level} · {r.uncertainty_count || 0}/24 unsure
              </span>
            )}
            <span className="chip"><Calendar className="w-3.5 h-3.5 text-[#9D4CDD]" strokeWidth={3} /> {lead.cta_clicked ? 'CTA clicked' : 'CTA not clicked'}</span>
            <span className="chip">{lead.whatsapp_clicked ? 'WhatsApp clicked' : 'WhatsApp not clicked'}</span>
          </div>
        </div>

        {/* Delivery Log */}
        <DeliveryLogCard lead={lead} onResend={resendReport} resending={resending} />
      </div>
    </div>
  );
}

function ZoneCareersCard({ zone, index, leadId, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(zone.careers || '');
  const [saving, setSaving] = useState(false);
  const label = ['Primary', 'Secondary', 'Exploration'][index];

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/leads/${leadId}/zone-careers`, {
        zone_index: index,
        careers: value.trim(),
      });
      toast.success(`Updated ${zone.name} careers`);
      onUpdated(value.trim());
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setValue(zone.careers || '');
    setEditing(false);
  };

  return (
    <div className="card-soft p-4 relative overflow-hidden" data-testid={`zone-card-${index}`}>
      <div className="absolute top-0 left-0 h-1.5 w-full" style={{ background: zone.color }} />
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: zone.color }}>
        {label}
      </div>
      <div className="font-display font-extrabold text-ink mt-1">{zone.name}</div>
      {zone.score !== undefined && <div className="text-xs text-muted-ink">Score: {zone.score}</div>}

      <div className="mt-3 pt-3 border-t border-[#E2E8F0]">
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-ink">
            What this could grow into
          </div>
          {!editing && (
            <button
              onClick={() => setEditing(true)}
              data-testid={`edit-careers-${index}`}
              className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#F1F5F9] text-ink hover:bg-[#E2E8F0] inline-flex items-center gap-1"
            >
              <Pencil className="w-3 h-3" strokeWidth={3} /> Edit
            </button>
          )}
        </div>

        {editing ? (
          <>
            <textarea
              value={value}
              onChange={(e) => setValue(e.target.value)}
              rows={4}
              placeholder="e.g. Software Engineer, Data Scientist, ML Engineer"
              data-testid={`careers-textarea-${index}`}
              className="w-full text-sm border border-[#CBD5E1] rounded-lg px-2 py-1.5 focus:outline-none focus:border-[#0055FF]"
            />
            <div className="flex gap-2 mt-2 justify-end">
              <button
                onClick={cancel}
                disabled={saving}
                className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-[#F1F5F9] text-ink hover:bg-[#E2E8F0] inline-flex items-center gap-1"
              >
                <X className="w-3 h-3" strokeWidth={3} /> Cancel
              </button>
              <button
                onClick={save}
                disabled={saving}
                data-testid={`save-careers-${index}`}
                className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-[#0055FF] text-white hover:bg-[#0044CC] inline-flex items-center gap-1 disabled:opacity-50"
              >
                <Check className="w-3 h-3" strokeWidth={3} /> {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </>
        ) : (
          <div className="text-sm text-body" data-testid={`careers-display-${index}`}>
            {zone.careers || <span className="text-muted-ink italic">Not set — click Edit to add.</span>}
          </div>
        )}
      </div>
    </div>
  );
}

function DeliveryLogCard({ lead, onResend, resending }) {
  const pdfErr = lead.full_report_pdf_error;
  const rows = [
    { label: 'Full Report Email', wa: lead.full_report_email, filename: lead.full_report_filename, generated_at: lead.full_report_generated_at },
  ].filter((r) => r.wa || r.filename);

  return (
    <div className="card-soft p-7" data-testid="delivery-log-card">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-[#0055FF]" strokeWidth={2.5} />
          <div className="font-display font-black text-ink text-xl">Email Delivery Log</div>
        </div>
        <button
          onClick={onResend}
          disabled={resending}
          data-testid="resend-report-btn"
          className="btn-secondary inline-flex items-center gap-2 !py-2 !px-4 text-sm disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${resending ? 'animate-spin' : ''}`} strokeWidth={3} />
          {resending ? 'Queuing…' : 'Resend Report'}
        </button>
      </div>

      {pdfErr && (
        <div className="mb-4 rounded-2xl border-2 border-[#FF5A00] bg-[#FFF4EC] p-4" data-testid="pdf-error-banner">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 bg-[#FF5A00] text-white">
              <XCircle strokeWidth={2.5} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-extrabold text-ink text-base">PDF generation failed</div>
              <div className="text-xs text-body mt-1">
                <span className="font-bold">{pdfErr.type}:</span> {pdfErr.message}
              </div>
              {pdfErr.at && (
                <div className="text-[11px] text-muted-ink mt-1">
                  <span className="font-bold">At:</span> {pdfErr.at.slice(0,19).replace('T',' ')}
                </div>
              )}
              {(pdfErr.trace_head?.length || pdfErr.trace_tail?.length) ? (
                <details className="mt-2">
                  <summary className="text-[11px] font-bold text-[#FF5A00] cursor-pointer">Show traceback</summary>
                  <pre className="text-[10px] leading-4 mt-2 p-3 bg-[#0F172A] text-[#E2E8F0] rounded-xl overflow-x-auto max-h-64">
{(pdfErr.trace_head || []).join('\n')}
{pdfErr.trace_head?.length && pdfErr.trace_tail?.length ? '\n...\n' : ''}
{(pdfErr.trace_tail || []).join('\n')}
                  </pre>
                </details>
              ) : null}
              <div className="text-[11px] text-muted-ink mt-2">
                Fix the root cause (e.g. missing chromium browser) and click <b>Resend Report</b> to retry.
              </div>
            </div>
          </div>
        </div>
      )}

      {rows.length === 0 && !pdfErr ? (
        <div className="text-sm text-muted-ink py-6 text-center border-2 border-dashed border-[#E2E8F0] rounded-2xl" data-testid="delivery-log-empty">
          No delivery attempts yet — click <b>Resend Report</b> to generate and email.
        </div>
      ) : (
        <ul className="space-y-3" data-testid="delivery-log-list">
          {rows.map((row) => {
            const wa = row.wa || {};
            const ok = Boolean(wa.ok);
            const skipped = Boolean(wa.skipped);
            const Icon = ok ? CheckCircle2 : (skipped ? AlertCircle : XCircle);
            const color = ok ? '#00D95F' : (skipped ? '#FFD600' : '#FF5A00');
            const label = ok ? 'Delivered' : (skipped ? 'Skipped' : (wa.reason ? 'Failed' : 'PDF only'));
            return (
              <li key={row.label} className="rounded-2xl border border-[#E2E8F0] p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${color}1A`, color }}>
                    <Icon strokeWidth={2.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="font-display font-extrabold text-ink text-base">{row.label}</div>
                      <span className="text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full" style={{ background: `${color}1A`, color }}>{label}</span>
                      {wa.provider && (
                        <span className="text-[10px] font-bold text-muted-ink">· via: <span className="text-ink">{wa.provider}</span></span>
                      )}
                    </div>
                    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 mt-2 text-xs text-body">
                      {wa.attempted_at && <div><span className="text-muted-ink font-bold">Attempted:</span> {wa.attempted_at.slice(0,19).replace('T',' ')}</div>}
                      {row.generated_at && <div><span className="text-muted-ink font-bold">PDF built:</span> {row.generated_at.slice(0,19).replace('T',' ')}</div>}
                      {wa.recipient && <div><span className="text-muted-ink font-bold">To:</span> {wa.recipient}</div>}
                      {wa.id && <div><span className="text-muted-ink font-bold">Email ID:</span> {wa.id}</div>}
                      {wa.reason && <div className="sm:col-span-2"><span className="text-muted-ink font-bold">Reason:</span> {String(wa.reason)}</div>}
                      {row.filename && (
                        <div className="sm:col-span-2 truncate">
                          <span className="text-muted-ink font-bold">PDF file:</span>{' '}
                          <span className="text-ink font-mono">{row.filename}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-ink">{label}</div>
      <div className="font-display font-extrabold text-ink mt-0.5">{value || '—'}</div>
    </div>
  );
}
